import { useState } from 'react';
import { useSnapshot } from 'valtio';

import Slider from '@cartwhl/ui/atoms/Slider';
import Text from '@cartwhl/ui/base/Text';

import { animationGenerationStore, sceneSettingsStore } from 'stores';
import { AnimationPlayerStoreInstance } from 'stores/animationPlayerStoreFactory';

export default function Timeline({
  animationPlayerStore,
  size = 'lg',
  order = 'default',
  hideCount = false,
}: {
  animationPlayerStore: AnimationPlayerStoreInstance;
  size?: 'lg' | 'sm';
  order?: 'default' | 'reverse';
  hideCount?: boolean;
}) {
  const { videoUpload, jobData, currentAnimationNumber } = useSnapshot(
    animationGenerationStore.state,
  );
  const { currentFrame, duration, trimFrameStart, trimFrameEnd } = useSnapshot(
    animationPlayerStore.state,
    { sync: true },
  );

  const {
    playbackSettings: { frameStepSize },
  } = useSnapshot(sceneSettingsStore.state);

  // TODO: What if it is from video, but there is o mp4path?
  const currentAnimation = jobData?.variationInfoByID[currentAnimationNumber];
  const isFromVideo = !!currentAnimation?.comicMp4Path;

  const [showDetails, setDetails] = useState(false);
  const isPlayerDisabled =
    videoUpload?.status === 'QUEUED' ||
    videoUpload?.status === 'PROCESSING' ||
    currentAnimation?.status !== 'COMPLETED';

  return (
    <div
      className="pointer-events-auto mx-auto w-full px-2 lg:px-0"
      style={{
        height: size === 'lg' ? '60px' : '40px',
      }}
    >
      <div
        className={`flex h-full items-center justify-end ${
          order === 'reverse' ? 'flex-row-reverse' : ''
        } ${size === 'sm' ? 'gap-2' : 'gap-4 lg:gap-6'}`}
      >
        <Slider
          currentValue={formatFrames(currentFrame)}
          min={formatFrames(0)}
          max={formatFrames(duration.frames - 1)}
          step={frameStepSize}
          onChange={(value) => {
            animationPlayerStore.setFrame(parseSliderValue(value));
          }}
          trackColor={isFromVideo ? 'fromVideo' : 'player'}
          rangeColor={isFromVideo ? 'fromVideo' : 'player'}
          disabled={isPlayerDisabled}
        />

        {!hideCount && (
          <div className="text-gray-90 relative flex h-full items-center">
            {showDetails && (
              <div
                className={`bg-gray-90/80 absolute bottom-full z-10 flex flex-col items-end justify-end rounded-lg py-2 text-[14px]/[18px] text-white ${
                  order === 'reverse' ? 'left-0' : 'right-0'
                }`}
              >
                <div className="grid grid-cols-[auto_auto] gap-x-3 whitespace-nowrap text-right font-medium">
                  <div className="text-gray-30">Current frame:</div>
                  <div>{currentFrame}</div>
                  {/* <div className="text-gray-30">Current time:</div>
                <div>{currentFrame * (duration.seconds / duration.frames)}</div> */}
                  <div className="text-gray-30">Duration:</div>
                  <div>{duration.frames}</div>
                  {/* <div className="text-gray-30">Duration (seconds):</div>
                <div>{duration.seconds}</div> */}
                  <div className="text-gray-30">TrimStart:</div>
                  <div>{trimFrameStart}</div>
                  <div className="text-gray-30">TrimEnd:</div>
                  <div>{trimFrameEnd}</div>
                </div>
              </div>
            )}

            <button onClick={() => setDetails((v) => !v)}>
              <Text
                as="div"
                variant="studio/body/medium/sm"
                className="text-gray-90 -ml-2 mr-2 text-end opacity-60"
                style={{
                  width: duration.frames.toString().length * 21,
                }}
              >
                {!!duration.frames && (
                  <>
                    {formatFrames(currentFrame)}/
                    {formatFrames(duration.frames - 1)}
                  </>
                )}
              </Text>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// We need to keep in mind that our system handles frames with "index zero"
// But the user should see "index one"
// The following functions seem trivial but they will help developers understand better what happens
// For sanity you can remove `+ 1` and `- 1` and see the actual numbers in the UI
function formatFrames(value: number) {
  return value + 1;
}
function parseSliderValue(value: number) {
  return value - 1;
}
